import { useState } from 'react';
import { ListboxWrapper, ListboxWrapperProps } from '@components/common/Forms';
import {
  Chip, ListboxStyled, ListboxStyledButton, ListboxStyledOptions, ListboxStyledOption,
} from '@components/common';
import { CheckIcon } from '@heroicons/react/20/solid';
import { RadioCheckedIcon } from '@components/common/Icons';
import { TFunction } from 'next-i18next';
import { filterStyles } from '../helpers/filterStyles';

// Interfaces
// **************************************
type OmitWrapperProps = 'withTranslation' | 'defaultValueKey' | 'labelKey' | 'renderListbox';

interface TopBarGroupProps<DataType> extends Omit<ListboxWrapperProps<DataType>, OmitWrapperProps> {
  clearGroup?: () => void,
  filterGroupName?: string,
  panelHelperText?: string,
  iconType?: 'radio' | 'checkbox',
  alwaysShow?: boolean,
  dropdownArrowPosition?: 'left' | 'right',
}


/**
 * @description The top bar filters group component. It's s stylized ListboxWrapper component.
 * ***clearGroup** - the function to clear the group. Defaults to undefined.
 * ***filterGroupName** - the name of the filter group. If present, it will show the number of selected items if more than 1 (ie: Domains: 3 selected), otherwise show the selected label (ie: Sales).
 * ***panelHelperText** - the helper text to show in the dropdown panel (ie: Domains). Defaults to undefined.
 */
export const TopBarGroup = <DataType extends { label: string }>(props: TopBarGroupProps<DataType>) => {
  // Destructure props
  const {
    data,
    clearGroup,
    submitFunc,
    multiple = false,
    fieldName,
    filterGroupName,
    panelHelperText,
    iconType = 'checkbox',
    alwaysShow,
    dropdownArrowPosition = 'left',
    ...rest
  } = props;

  // show/hide the component
  const [hidden, setHidden] = useState(false);

  // Handle the clear group
  const clearFilterGroup = () => {
    setHidden(true);
    if (clearGroup) clearGroup();
    if (submitFunc) setTimeout(() => submitFunc(), 50);
  };

  // Render label
  // **************************************
  const renderLabel = (selected: DataType | DataType[], selectedLabel: string, t: TFunction) => {
    if (filterGroupName) {
      if (Array.isArray(selected) && selected.length > 1) {
        return `${t(filterGroupName)}: ${selected.length} ${t('label.selected.multiple')}`;
      }
      return `${t(filterGroupName)}: ${selectedLabel || t('global.action.choose')}`;
    }
    return selectedLabel || t('global.action.choose');
  };


  // Render component
  // **************************************
  return (
    <ListboxWrapper
      fieldName={fieldName}
      data={data}
      defaultValueKey="recordOptions.defaultValue"
      labelKey="recordOptions.filtersLabel"
      withTranslation
      multiple={multiple}
      renderListbox={({
        selectedLabel, lsData, selected, setSelected, t,
      }) => (
        <ListboxStyled
          className={`relative${!alwaysShow && (hidden || !selectedLabel) ? ' hidden' : ''}`}
          by="label"
          value={selected as DataType}
          onChange={setSelected}
          multiple={multiple}
        >
          {({ open }) => (
            <>
              {/* Dropdown trigger */}
              <ListboxStyledButton
                as={Chip}
                withDropdownArrow
                isDropdownOpen={open}
                dropdownArrowPosition={dropdownArrowPosition}
                onDelete={clearGroup ? () => clearFilterGroup() : undefined}
              >
                {renderLabel(selected, selectedLabel, t)}
              </ListboxStyledButton>

              {/* DROPDOWN PANEL */}
              <ListboxStyledOptions
                static
                className={`${filterStyles.listboxOptionsWrapper}`}
                animation="slideDown"
                anchor="bottom start"
              >
                { // If we have the panelHelperText, show it
                panelHelperText && <span className="my-2 block px-3.5 text-sm text-ink-light">{t(panelHelperText)}</span>
              }

                {/* List of options; selected state comes from the Headless render prop —
                    data-selected only lands on the option element, never on the icons */}
                {lsData.map((field) => (
                  <ListboxStyledOption
                    key={field?.label}
                    value={field}
                    className={`${filterStyles.listboxOption} ${iconType === 'checkbox' ? 'gap-2' : 'gap-1.5'}`}
                    onClick={submitFunc}
                  >
                    {({ selected: isSelected }) => (
                      <>
                        {iconType === 'checkbox' && (
                          <span
                            className={`flex size-4 shrink-0 items-center justify-center rounded border transition-colors ${
                              isSelected ? 'border-primary bg-primary' : 'border-input bg-surface'
                            }`}
                          >
                            {isSelected && <CheckIcon className="size-3.5 text-white" />}
                          </span>
                        )}
                        {iconType === 'radio' && (
                          <RadioCheckedIcon
                            className={isSelected
                              ? 'h-5 w-5 text-primary [&>path:first-of-type]:block'
                              : filterStyles.listboxOptionCheck}
                          />
                        )}
                        {t(field?.label)}
                      </>
                    )}
                  </ListboxStyledOption>
                ))}
              </ListboxStyledOptions>
            </>
          )}
        </ListboxStyled>
      )}
      {...rest}
    />
  );
};
